// Book panel (B) — SPEC §15.1 / §10: "Book into" (which of the setter links: APPROVAL-three-links 6.2), Calendly slots
// in the prospect's time, invitee details, the chosen link's booking questions, the reminder-text box, the say-back
// line, and a manual fallback when Calendly is down or they booked elsewhere ("Which meeting did they book?").
// Three ways since PJ 2026-10-20 ("there's an option to mark it if you booked it through the Calendly link or to book
// it through the dialer itself"): Book a time | They booked on the link (BookLinked.tsx: the booking they made on a
// Calendly link, linked to this lead) | Booked elsewhere. On a no-campaign pad call the panel opens on the link list;
// Book a time needs a campaign lead (the slots and the booking questions are the campaign's), so it is off there.
import { useEffect, useRef, useState } from 'preact/hooks';
import type { QueueItem, Slot } from '../../../src/shared/api';
import { REMINDER_TEXT_QUESTION, bookingLinkName, bookingOptions, setterLink, type BookingOptions } from '../../../src/shared/calendly';
import { ApiFailure, errorMessage } from '../api';
import { ErrorBanner, toast } from '../components/ui';
import { Field, Spinner } from '../components/ui-extra';
import { endpoints, type BookingQuestionsView } from './endpoints';
import { buildBookRequest, dispensaryBooking, sayBackLines, textReminderOffered } from './book';
import { LinkedBookings } from './BookLinked';
import { BOOK_MODES, bookTimeProblem, initialBookMode, type BookMode } from './linked';
import { toE164Nanp } from './labels';
import { onMeetingBooked, type WrapUp } from './store';
import { fmtDayTime, fmtTime, fromDatetimeLocal, localDateKey, outsideDefaultHours, tzAbbrev } from './time';

const EMAIL = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

export function BookPanel(props: { item: QueueItem; campaignSlug: string | null; callId: number | null; wrap: WrapUp; onClose: () => void }) {
  const b = props.item.business;
  const membershipId = props.item.membership.id;
  const campaignNiche = props.item.campaign.niche;
  // A no-campaign pad call (membership 0): no slots or questions to load; the link list and Booked elsewhere work.
  const pad = !!props.item.noCampaign;
  const padWhy = bookTimeProblem(props.item);
  // The panel's own view of the lock until the server answers (the server also knows the business's other campaigns).
  const [options, setOptions] = useState<BookingOptions>(() => bookingOptions(props.campaignSlug, dispensaryBooking([b.niche, campaignNiche])));
  // null = the lead's default link (the server resolves it); a slug once the setter picks one.
  const [picked, setPicked] = useState<string | null>(null);
  const [mode, setMode] = useState<BookMode>(() => initialBookMode(props.item));
  const [slots, setSlots] = useState<Slot[] | null>(null);
  const [slotsError, setSlotsError] = useState<string | null>(null);
  const [slot, setSlot] = useState<string | null>(null);
  const [name, setName] = useState(props.wrap.dmName || b.dmName || '');
  const [email, setEmail] = useState(props.wrap.email || '');
  const [mobile, setMobile] = useState('');
  const [bookingSystem, setBookingSystem] = useState('');
  // null = loading (Book stays off until the chosen link's questions are in).
  const [questions, setQuestions] = useState<BookingQuestionsView | null>(null);
  const [questionsError, setQuestionsError] = useState<string | null>(null);
  const [afterHours, setAfterHours] = useState('');
  const [note, setNote] = useState('');
  const [textOk, setTextOk] = useState(false);
  const [manualAt, setManualAt] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // The link in use: the one picked, else the server's default (questions.slug), else the panel's own default.
  const eventSlug = picked ?? questions?.slug ?? options.defaultSlug;

  // A late answer for a link the setter already switched away from is dropped: otherwise an older questions answer
  // could land last (Book stuck on "Loading") or an older link's times could be booked on the new link.
  const questionsSeq = useRef(0);
  const slotsSeq = useRef(0);

  const loadQuestions = () => {
    if (pad) return;
    const seq = ++questionsSeq.current;
    setQuestions(null);
    setQuestionsError(null);
    endpoints.bookingQuestions(membershipId, picked).then((q) => {
      if (seq !== questionsSeq.current) return;
      setQuestions(q);
      if (q.options) setOptions(q.options);
    }, (e) => { if (seq === questionsSeq.current) setQuestionsError(errorMessage(e)); });
  };
  useEffect(loadQuestions, [membershipId, picked]);

  const loadSlots = () => {
    if (pad) return;
    const seq = ++slotsSeq.current;
    setSlots(null);
    setSlotsError(null);
    endpoints.slots(membershipId, picked).then(
      (s) => { if (seq === slotsSeq.current) setSlots(s); },
      (e) => { if (seq === slotsSeq.current) setSlotsError(errorMessage(e)); },
    );
  };
  useEffect(loadSlots, [membershipId, picked]);

  /** "Book into": a new link clears the time, the two select answers and the text tick; name, email, cell and note stay. */
  function pick(slug: string) {
    if (slug === eventSlug) return;
    setPicked(slug);
    setSlot(null);
    setBookingSystem('');
    setAfterHours('');
    setTextOk(false);
    setError(null);
  }

  let tzLabel = b.tz;
  try { tzLabel = `${b.tz.replace(/_/g, ' ')} (${tzAbbrev(new Date(), b.tz)})`; } catch { /* keep the raw zone */ }

  const groups: Array<{ day: string; slots: Slot[] }> = [];
  for (const s of slots ?? []) {
    let key = s.startTime.slice(0, 10);
    try { key = localDateKey(new Date(s.startTime), b.tz); } catch { /* raw date */ }
    const g = groups.find((x) => x.day === key);
    if (g) g.slots.push(s); else groups.push({ day: key, slots: [s] });
  }
  const dayLabel = (s: Slot) => {
    try { return new Date(s.startTime).toLocaleDateString('en-US', { timeZone: b.tz, weekday: 'long', month: 'short', day: 'numeric' }); } catch { return s.label; }
  };
  const timeLabel = (s: Slot) => {
    try { return fmtTime(new Date(s.startTime), b.tz); } catch { return s.label; }
  };

  const mobileE164 = mobile.trim() ? toE164Nanp(mobile) : null;
  const chosen = slots?.find((s) => s.startTime === slot) ?? null;
  const questionsReady = !!questions && questions.slug === eventSlug;
  const textAsk = textReminderOffered(eventSlug);
  const cannabis = options.locked;

  function problem(): string | null {
    if (mode === 'link') return 'Pick their booking in the list, then press Link.';
    if (mode === 'calendly') {
      if (padWhy) return padWhy;
      if (!questionsReady) return 'Wait for the booking questions to load.';
      if (!chosen) return 'Pick a time.';
      if (!name.trim()) return "Enter the person's name.";
      if (!EMAIL.test(email.trim())) return 'Enter a valid email; Calendly sends the confirmation there.';
      if (mobile.trim() && !mobileE164) return "That cell number doesn't look right (10 digits, North American).";
      if (questions?.primary && !bookingSystem) return `Ask: ${questions.primary.name}`;
      return null;
    }
    if (!fromDatetimeLocal(manualAt, b.tz)) return 'Pick the meeting date and time (their time).';
    if (email.trim() && !EMAIL.test(email.trim())) return "That email doesn't look right.";
    return null;
  }

  async function submit(e: Event) {
    e.preventDefault();
    if (mode === 'link') return; // the list links with its own button
    const p = problem();
    if (p) { setError(p); return; }
    setBusy(true);
    setError(null);
    try {
      if (mode === 'calendly') {
        const meeting = await endpoints.book(buildBookRequest(membershipId, props.callId, {
          startTime: chosen!.startTime, name, email, mobileE164, bookingSystem, afterFive: afterHours, note, textOk, eventSlug,
        }));
        toast(`Booked ${b.name} on ${bookingLinkName(eventSlug)}: ${chosen!.label}. Calendly emails them the confirmation.`, 'ok');
        props.onClose();
        await onMeetingBooked(meeting, 'calendly');
      } else {
        const at = fromDatetimeLocal(manualAt, b.tz)!;
        // Only a link the setter picked or the server named: when Calendly is down the questions never load, and the
        // panel's own guess (no campaign loaded = Your growth call) must not override the server's default for the lead.
        const meeting = await endpoints.bookManual({
          ...(pad ? { businessId: b.id } : { membershipId }), callId: props.callId ?? undefined, scheduledFor: at.toISOString(),
          name: name.trim() || undefined, email: email.trim() || undefined, note: note.trim() || undefined,
          eventSlug: picked ?? questions?.slug ?? undefined,
        });
        const loggedOn = picked ?? questions?.slug;
        toast(`Meeting logged for ${b.name}${loggedOn ? ` (${bookingLinkName(loggedOn)})` : ''}: ${fmtDayTime(at, b.tz)}.`, 'ok');
        props.onClose();
        await onMeetingBooked(meeting, 'manual');
      }
    } catch (err) {
      if (err instanceof ApiFailure && err.body.error === 'slot_taken') {
        toast('That time was just taken. Pick another.', 'warn');
        setSlot(null);
        loadSlots();
      } else {
        setError(`${errorMessage(err)}${mode === 'calendly' && err instanceof ApiFailure && err.status >= 500 ? ' If Calendly is down, use "Booked elsewhere".' : ''}`);
      }
    } finally {
      setBusy(false);
    }
  }

  const manualDate = fromDatetimeLocal(manualAt, b.tz);
  const manualWarn = manualDate ? outsideDefaultHours(manualDate, b.tz) : null;
  const sayBack = chosen ? sayBackLines(eventSlug, `${dayLabel(chosen)} at ${timeLabel(chosen)}`) : null;

  return (
    <form class="vd-book" onSubmit={submit}>
      <div class="row" style="justify-content:space-between;gap:8px;margin-bottom:8px">
        <p class="muted" style="margin:0">Times in their zone: {tzLabel}. Offer two, next business day first.</p>
        <button type="button" class="btn btn-sm" onClick={props.onClose}>Close <kbd>Esc</kbd></button>
      </div>

      <div class="row" style="gap:6px;margin-bottom:8px;flex-wrap:wrap" role="group" aria-label="How the meeting was booked">
        {BOOK_MODES.map((m) => (
          <button type="button" class={`btn btn-sm ${mode === m.mode ? 'btn-ink' : ''}`} aria-pressed={mode === m.mode}
            disabled={busy || (m.mode === 'calendly' && !!padWhy)} title={m.mode === 'calendly' && padWhy ? padWhy : undefined}
            onClick={() => { setMode(m.mode); setError(null); }}>{m.label}</button>
        ))}
      </div>
      {padWhy ? <p class="vd-field-hint" style="margin:0 0 8px">{padWhy}</p> : null}

      {mode === 'link' ? (
        <LinkedBookings item={props.item} callId={props.callId} onElsewhere={() => setMode('manual')}
          onLinked={async (meeting) => { props.onClose(); await onMeetingBooked(meeting, 'link'); }} />
      ) : <>
      <fieldset class="vd-book-into" style="border:0;padding:0;margin:0 0 8px">
        <legend class="eyebrow" style="padding:0">{mode === 'calendly' ? 'Book into' : 'Which meeting did they book?'}</legend>
        {cannabis ? (
          <p class="vd-field-hint" style="margin:4px 0">Cannabis: books on Your store's growth call.</p>
        ) : options.slugs.map((slug) => {
          const link = setterLink(slug);
          return (
            <label class="row" style="gap:6px;align-items:flex-start;margin:4px 0">
              <input type="radio" name="vd-book-into" value={slug} checked={slug === eventSlug} disabled={busy} onChange={() => pick(slug)} />
              <span><strong>{bookingLinkName(slug)}</strong>{slug === props.campaignSlug ? <span class="muted"> (this campaign's meeting)</span> : null}
                <br /><span class="vd-field-hint">{link ? link.whoFor : "This campaign's own booking link."}</span></span>
            </label>
          );
        })}
      </fieldset>

      {mode === 'calendly' ? (
        <div class="vd-slots">
          {slotsError ? (
            <div><ErrorBanner error={`Couldn't load times: ${slotsError}`} /><button type="button" class="btn btn-sm" onClick={loadSlots}>Try again</button></div>
          ) : slots === null ? <p class="muted"><Spinner /> Loading open times…</p>
            : slots.length === 0 ? <p class="muted">No open times in the next 7 days. Log it manually if you agreed a time, or set a callback.</p>
              : groups.map((g) => (
                <div class="vd-slot-day">
                  <div class="eyebrow">{dayLabel(g.slots[0])}</div>
                  <div class="row" style="gap:6px;flex-wrap:wrap">
                    {g.slots.map((s) => (
                      <button type="button" title={s.label} class={`btn btn-sm ${slot === s.startTime ? 'btn-primary' : ''}`} onClick={() => setSlot(s.startTime)}>{timeLabel(s)}</button>
                    ))}
                  </div>
                </div>
              ))}
        </div>
      ) : (
        <Field label="Meeting date and time (their time)" hint={manualWarn ?? undefined}>
          <input type="datetime-local" value={manualAt} onInput={(e) => setManualAt(e.currentTarget.value)} />
        </Field>
      )}

      <div class="vd-book-grid">
        <Field label="Their name"><input value={name} onInput={(e) => setName(e.currentTarget.value)} autocomplete="off" /></Field>
        <Field label="Their email"><input type="email" value={email} onInput={(e) => setEmail(e.currentTarget.value)} autocomplete="off" /></Field>
        {mode === 'calendly' ? (
          <>
            <Field label="Best cell for the call and the reminder text" hint={mobile && !mobileE164 ? 'Ten digits, North American number.' : 'Leave blank and Calendly gets the business line.'}>
              <input type="tel" value={mobile} onInput={(e) => setMobile(e.currentTarget.value)} placeholder="(555) 555-5555" autocomplete="off" />
            </Field>
            {questionsError ? (
              <div class="vd-span2"><ErrorBanner error={`Couldn't load the booking questions: ${questionsError}`} /><button type="button" class="btn btn-sm" onClick={loadQuestions}>Try again</button></div>
            ) : !questionsReady ? (
              <p class="muted vd-span2"><Spinner /> Loading the booking questions…</p>
            ) : (
              <>
                {questions!.primary ? (
                  <Field label={questions!.primary.name}>
                    <select value={bookingSystem} onChange={(e) => setBookingSystem(e.currentTarget.value)}>
                      <option value="">Choose…</option>
                      {questions!.primary.choices.map((o) => <option value={o}>{o}</option>)}
                    </select>
                  </Field>
                ) : null}
                {questions!.afterFive ? (
                  <Field label={questions!.afterFive.name} hint={afterHours ? undefined : "If you skip it, nothing is sent for it and the note says it wasn't asked."}>
                    <select value={afterHours} onChange={(e) => setAfterHours(e.currentTarget.value)}>
                      <option value="">Not asked</option>
                      {questions!.afterFive.choices.map((o) => <option value={o}>{o}</option>)}
                    </select>
                  </Field>
                ) : null}
              </>
            )}
          </>
        ) : null}
        <Field label="Anything they want us to look at first?" class="vd-span2">
          <textarea rows={2} value={note} onInput={(e) => setNote(e.currentTarget.value)} placeholder="Their words. Both topics? Book the one they said yes to and write the other here." />
        </Field>
      </div>

      {mode === 'calendly' && textAsk ? (
        <>
          <label class="row" style="gap:6px;margin:6px 0">
            <input type="checkbox" checked={textOk} disabled={!mobileE164} onChange={(e) => setTextOk(e.currentTarget.checked)} />
            <span>Asked "{REMINDER_TEXT_QUESTION}" and they gave this cell</span>
          </label>
          <p class="vd-field-hint" style="margin:0 0 6px">Calendly texts one reminder 2 hours before the call. Tick it only when they gave the cell for it.{cannabis ? ' Our own Text button stays off for cannabis.' : ''}</p>
        </>
      ) : null}
      {mode === 'calendly' && !textAsk ? (
        <p class="vd-field-hint" style="margin:6px 0">No reminder text on this meeting. Calendly's confirmation goes to their email.</p>
      ) : null}

      <ErrorBanner error={error} />
      {mode === 'calendly' && sayBack ? (
        <p class="vd-field-hint" style="margin:6px 0">Say it back: "{sayBack[0]}" Then: "{sayBack[1]}"</p>
      ) : null}
      <div class="row" style="gap:8px;margin-top:6px">
        <button type="submit" class="btn btn-primary" disabled={busy || (mode === 'calendly' && !questionsReady)}>
          {busy ? 'Booking…' : mode === 'calendly' ? (chosen ? `Book ${timeLabel(chosen)}` : 'Book') : 'Log meeting'}
        </button>
        {props.callId === null ? <span class="muted" style="font-size:13px">Not linked to a call.</span> : <span class="muted" style="font-size:13px">The call is logged as "Meeting booked".</span>}
      </div>
      </>}
    </form>
  );
}
